import React, { useState } from "react";
import Sidebar from "./Sidebar";
import "./manageClassExams.css";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileAlt,
  faCalendarAlt,
  faChartLine,
  faClock,
  faEye,
  faPlus,
  faDownload,
  faBell,
  faCog,
  faArrowRight,
  faGraduationCap,
  faPen,
  faChartBar,
  faList,
  faFileExcel,
} from "@fortawesome/free-solid-svg-icons";

const ManageClassExams = () => {
  const navigate = useNavigate();
  const [showOverview, setShowOverview] = useState(false); // Default state = hidden

  // Class names with enhanced data
  const classes = [
    {
      name: "Play Group",
      exams: { scheduled: 4, completed: 3, pending: 1 },
      status: "active",
      color: "pastel-blue",
      icon: faGraduationCap,
      sections: ["A", "B"],
    },
    {
      name: "Nursery",
      exams: { scheduled: 5, completed: 4, pending: 1 },
      status: "active",
      color: "pastel-lavender",
      icon: faGraduationCap,
      sections: ["A", "B"],
    },
    {
      name: "Prep",
      exams: { scheduled: 6, completed: 5, pending: 1 },
      status: "active",
      color: "pastel-mint",
      icon: faGraduationCap,
      sections: ["A", "B", "C"],
    },
    {
      name: "Class 1",
      exams: { scheduled: 8, completed: 7, pending: 1 },
      status: "excellent",
      color: "pastel-coral",
      icon: faGraduationCap,
      sections: ["A", "B", "C"],
    },
    {
      name: "Class 2",
      exams: { scheduled: 7, completed: 6, pending: 1 },
      status: "active",
      color: "pastel-peach",
      icon: faGraduationCap,
      sections: ["A", "B"],
    },
    {
      name: "Class 3",
      exams: { scheduled: 9, completed: 8, pending: 1 },
      status: "excellent",
      color: "pastel-aqua",
      icon: faGraduationCap,
      sections: ["A", "B", "C"],
    },
    {
      name: "Class 4",
      exams: { scheduled: 7, completed: 6, pending: 1 },
      status: "active",
      color: "pastel-lilac",
      icon: faGraduationCap,
      sections: ["A", "B"],
    },
    {
      name: "Class 5",
      exams: { scheduled: 10, completed: 9, pending: 1 },
      status: "excellent",
      color: "pastel-sunshine",
      icon: faGraduationCap,
      sections: ["A", "B", "C"],
    },
    {
      name: "Class 6",
      exams: { scheduled: 8, completed: 7, pending: 1 },
      status: "active",
      color: "pastel-rose",
      icon: faGraduationCap,
      sections: ["A", "B"],
    },
    {
      name: "Class 7",
      exams: { scheduled: 7, completed: 6, pending: 1 },
      status: "active",
      color: "pastel-sky",
      icon: faGraduationCap,
      sections: ["A", "B", "C"],
    },
    {
      name: "Class 8",
      exams: { scheduled: 11, completed: 10, pending: 1 },
      status: "excellent",
      color: "pastel-lime",
      icon: faGraduationCap,
      sections: ["A", "B", "C"],
    },
    {
      name: "Class Pre 9",
      exams: { scheduled: 5, completed: 4, pending: 1 },
      status: "warning",
      color: "pastel-sand",
      icon: faGraduationCap,
      sections: ["A", "B"],
    },
    {
      name: "Class 9",
      exams: { scheduled: 12, completed: 11, pending: 1 },
      status: "excellent",
      color: "pastel-blue",
      icon: faGraduationCap,
      sections: ["Science", "Arts", "Commerce"],
    },
    {
      name: "Class 10",
      exams: { scheduled: 14, completed: 13, pending: 1 },
      status: "excellent",
      color: "pastel-lavender",
      icon: faGraduationCap,
      sections: ["Science", "Arts", "Commerce"],
    },
  ];

  // Corresponding image filenames
  const imageFilenames = [
    "pg.png",
    "nur.png",
    "prep.png",
    "1.png",
    "2.png",
    "3.png",
    "4.png",
    "5.png",
    "6.png",
    "7.png",
    "8.png",
    "8.png",
    "9.png",
    "10.png",
  ];

  // Main class click - goes to add marks
  const handleClassClick = (className) => {
    navigate("/Exam-Marks-Sheet", { state: { className } });
  };

  // Updated handleQuickAction function
  const handleQuickAction = (action, className, e) => {
    e.stopPropagation();
    switch (action) {
      case "view":
        // Navigate to View Exam Records screen
        navigate("/exam-records", {
          state: {
            className: className,
            section: "All Sections",
          },
        });
        break;
      case "add":
        navigate("/Add-Exam", { state: { className } });
        break;
      case "reports":
        navigate("/Exam-Reports", { state: { className } });
        break;
      case "schedule":
        navigate("/Exam-Schedule", { state: { className } });
        break;
      default:
        break;
    }
  };

  // Alternative: Click directly on "View Exam Records" in stats section
  const handleViewExamRecords = (className, e) => {
    e.stopPropagation();
    navigate("/exam-records", {
      state: {
        className: className,
        section: "All Sections",
      },
    });
  };

  // Handle Add Marks click
  const handleAddMarks = (className, e) => {
    e.stopPropagation();
    navigate("/Exam-Marks-Sheet", { state: { className } });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "excellent":
        return "#10b981";
      case "active":
        return "#3b82f6";
      case "warning":
        return "#f59e0b";
      default:
        return "#3b82f6";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "excellent":
        return "Excellent";
      case "active":
        return "Active";
      case "warning":
        return "Needs Attention";
      default:
        return "Active";
    }
  };

  return (
    <div className="class-exams-screen">
      <div className="class-exams-dashboard-wrapper">
        <Sidebar />
        <main className="class-exams-main-content">
          {/* Header Section */}
          <div className="class-exams-header">
            <div className="class-exams-header-left">
              <div className="class-exams-title-section">
                <h1 className="class-exams-main-title">
                  <FontAwesomeIcon
                    icon={faFileAlt}
                    className="class-exams-title-icon"
                  />
                  Exams Management 2024-25
                </h1>
                <p className="class-exams-subtitle">
                  Manage examination schedules, student marks, and generate
                  detailed reports
                </p>
              </div>

              {/* Quick Stats */}
              <div className="class-exams-meta-stats">
                <div className="class-exams-meta-stat">
                  <span className="class-exams-meta-value">14</span>
                  <span className="class-exams-meta-label">Active Classes</span>
                </div>
                <div className="class-exams-meta-divider">•</div>
                <div className="class-exams-meta-stat">
                  <span className="class-exams-meta-value">126</span>
                  <span className="class-exams-meta-label">Total Exams</span>
                </div>
                <div className="class-exams-meta-divider">•</div>
                <div className="class-exams-meta-stat">
                  <span className="class-exams-meta-value">89%</span>
                  <span className="class-exams-meta-label">
                    Completion Rate
                  </span>
                </div>
              </div>
            </div>

            <div className="class-exams-header-actions">
              {/* --- "Create New Exam" Button Commented Out --- */}
              {/* 
              <button
                className="class-exams-action-btn class-exams-primary-action"
                onClick={() =>
                  navigate("/Exam-Marks-Sheet", {
                    state: { className: "Select Class" },
                  })
                }
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Create New Exam</span>
              </button> 
              */}
              <button className="class-exams-action-btn class-exams-secondary-action">
                <FontAwesomeIcon icon={faFileExcel} />
                <span>Export Reports</span>
              </button>
            </div>
          </div>

          {/* Show/Hide Toggle for Dashboard Overview */}
          <div className="class-exams-toggle-container" onClick={() => setShowOverview(!showOverview)}>
            <span className="class-exams-toggle-text">
              {showOverview ? "Hide Dashboard Stats" : "Show Dashboard Stats"}
            </span>
            <div className={`class-exams-toggle-switch ${showOverview ? "active" : ""}`}>
              <div className="class-exams-toggle-slider"></div>
            </div>
          </div>

          {/* Dashboard Overview (Hidden by default) */}
          {showOverview && (
            <div className="class-exams-dashboard-overview">
              <div className="class-exams-overview-card class-exams-stats-card">
                <div className="class-exams-stats-grid">
                  <div className="class-exams-stat-item">
                    <div className="class-exams-stat-icon class-exams-stat-blue">
                      <FontAwesomeIcon icon={faFileAlt} />
                    </div>
                    <div className="class-exams-stat-content">
                      <div className="class-exams-stat-number">142</div>
                      <div className="class-exams-stat-text">Total Exams</div>
                    </div>
                  </div>
                  <div className="class-exams-stat-item">
                    <div className="class-exams-stat-icon class-exams-stat-green">
                      <FontAwesomeIcon icon={faChartLine} />
                    </div>
                    <div className="class-exams-stat-content">
                      <div className="class-exams-stat-number">126</div>
                      <div className="class-exams-stat-text">Completed</div>
                    </div>
                  </div>
                  <div className="class-exams-stat-item">
                    <div className="class-exams-stat-icon class-exams-stat-orange">
                      <FontAwesomeIcon icon={faClock} />
                    </div>
                    <div className="class-exams-stat-content">
                      <div className="class-exams-stat-number">16</div>
                      <div className="class-exams-stat-text">Pending</div>
                    </div>
                  </div>
                  <div className="class-exams-stat-item">
                    <div className="class-exams-stat-icon class-exams-stat-purple">
                      <FontAwesomeIcon icon={faCalendarAlt} />
                    </div>
                    <div className="class-exams-stat-content">
                      <div className="class-exams-stat-number">28</div>
                      <div className="class-exams-stat-text">Upcoming</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="class-exams-overview-card class-exams-actions-card">
                <h3 className="class-exams-actions-title">Quick Actions</h3>
                <div className="class-exams-actions-grid">
                  <button
                    className="class-exams-quick-action"
                    onClick={() =>
                      navigate("/exam-records", {
                        state: {
                          className: "All Classes",
                          section: "All Sections",
                        },
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faList} />
                    <span>View All Records</span>
                  </button>
                  <button
                    className="class-exams-quick-action"
                    onClick={() =>
                      navigate("/Exam-Schedule", {
                        state: { className: "All Classes" },
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faBell} />
                    <span>Exam Schedule</span>
                  </button>
                  <button
                    className="class-exams-quick-action"
                    onClick={() =>
                      navigate("/Exam-Reports", {
                        state: { className: "All Classes" },
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faCog} />
                    <span>Reports</span>
                  </button>
                  <button
                    className="class-exams-quick-action"
                    onClick={() =>
                      navigate("/Exam-Calendar", {
                        state: { className: "All Classes" },
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    <span>Calendar View</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Classes Grid Section */}
          <div className="class-exams-grid-section">
            <div className="class-exams-grid-header">
              <h2 className="class-exams-grid-title">
                <FontAwesomeIcon icon={faGraduationCap} />
                Select Class for Exam Management
              </h2>
              <div className="class-exams-grid-actions">
                <button className="class-exams-grid-action active">
                  All Classes
                </button>
                <button className="class-exams-grid-action">Active</button>
                <button className="class-exams-grid-action">
                  High Priority
                </button>
              </div>
            </div>

            {/* Perfectly Balanced Grid */}
            <div className="class-exams-grid-container">
              {classes.map((cls, index) => (
                <div
                  key={index}
                  className={`class-exams-card class-exams-card-${cls.color}`}
                  onClick={() => handleClassClick(cls.name)}
                >
                  {/* Card Top Section with Image */}
                  <div className="class-exams-card-top">
                    <div className="class-exams-card-image-wrapper">
                      <div className="class-exams-card-image-container">
                        <img
                          src={`/Classes/${imageFilenames[index]}`}
                          alt={cls.name}
                          className="class-exams-card-image"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/Classes/default.png";
                          }}
                        />
                        <div className="class-exams-card-image-overlay"></div>
                      </div>

                      {/* Status Badge - Perfectly Positioned */}
                      <div
                        className="class-exams-card-status"
                        style={{ backgroundColor: getStatusColor(cls.status) }}
                      >
                        {getStatusLabel(cls.status)}
                      </div>
                    </div>

                    {/* Class Info */}
                    <div className="class-exams-card-info">
                      <h3 className="class-exams-card-title">{cls.name}</h3>
                      <div className="class-exams-card-subtitle">
                        {cls.name.includes("Play Group") ||
                        cls.name.includes("Nursery") ||
                        cls.name.includes("Prep")
                          ? "Pre-School Level"
                          : `Grade ${cls.name.replace("Class ", "")}`}
                      </div>
                      <div className="class-exams-card-sections">
                        {cls.sections.map((section, idx) => (
                          <span key={idx} className="class-exams-section-tag">
                            {section}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Card Middle Section - Stats with Direct Action Buttons */}
                  <div className="class-exams-card-middle">
                    <div className="class-exams-card-stats">
                      <div className="class-exams-stat-pair">
                        <div
                          className="class-exams-stat-item-compact clickable"
                          onClick={(e) => handleAddMarks(cls.name, e)}
                          title="Click to Add Marks"
                        >
                          <div className="class-exams-stat-icon-compact">
                            <FontAwesomeIcon icon={faPen} />
                          </div>
                          <div className="class-exams-stat-content-compact">
                            <div className="class-exams-stat-number-compact">
                              {cls.exams.scheduled}
                            </div>
                            <div className="class-exams-stat-label-compact">
                              Add Marks
                            </div>
                          </div>
                        </div>

                        <div
                          className="class-exams-stat-item-compact clickable"
                          onClick={(e) => handleViewExamRecords(cls.name, e)}
                          title="Click to View Exam Records"
                        >
                          <div className="class-exams-stat-icon-compact">
                            <FontAwesomeIcon icon={faChartBar} />
                          </div>
                          <div className="class-exams-stat-content-compact">
                            <div className="class-exams-stat-number-compact">
                              {cls.exams.completed}
                            </div>
                            <div className="class-exams-stat-label-compact">
                              View Exam Records
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="class-exams-progress-container">
                        <div className="class-exams-progress-header">
                          <span className="class-exams-progress-label">
                            Progress
                          </span>
                          <span className="class-exams-progress-percentage">
                            {Math.round(
                              (cls.exams.completed / cls.exams.scheduled) * 100
                            )}
                            %
                          </span>
                        </div>
                        <div className="class-exams-progress-bar">
                          <div
                            className="class-exams-progress-fill"
                            style={{
                              width: `${
                                (cls.exams.completed / cls.exams.scheduled) *
                                100
                              }%`,
                              backgroundColor: getStatusColor(cls.status),
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Bottom Section - Actions */}
                  <div className="class-exams-card-bottom">
                    <div className="class-exams-card-actions">
                      <button
                        className="class-exams-card-action class-exams-action-view"
                        onClick={(e) => handleQuickAction("view", cls.name, e)}
                        title="View All Exam Records"
                      >
                        <FontAwesomeIcon icon={faEye} />
                        <span>View Exams</span>
                      </button>
                      <button
                        className="class-exams-card-action class-exams-action-schedule"
                        onClick={(e) =>
                          handleQuickAction("schedule", cls.name, e)
                        }
                        title="Schedule Exams"
                      >
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        <span>Schedule</span>
                      </button>
                    </div>

                    {/* Hover Overlay */}
                    <div className="class-exams-card-hover-overlay">
                      <div className="class-exams-hover-content">
                        <FontAwesomeIcon
                          icon={faArrowRight}
                          className="class-exams-hover-icon"
                        />
                        <span className="class-exams-hover-text">
                          Manage All Exams
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Section */}
          <div className="class-exams-footer">
            <div className="class-exams-footer-content">
              <div className="class-exams-footer-info">
                <div className="class-exams-footer-stat">
                  <span className="class-exams-footer-stat-value">
                    {classes.length}
                  </span>
                  <span className="class-exams-footer-stat-label">
                    Classes Available
                  </span>
                </div>
                <div className="class-exams-footer-divider"></div>
                <div className="class-exams-footer-stat">
                  <span className="class-exams-footer-stat-value">2024-25</span>
                  <span className="class-exams-footer-stat-label">
                    Academic Year
                  </span>
                </div>
                <div className="class-exams-footer-divider"></div>
                <div className="class-exams-footer-stat">
                  <span className="class-exams-footer-stat-value">Live</span>
                  <span className="class-exams-footer-stat-label">
                    System Status
                  </span>
                </div>
              </div>

              <div className="class-exams-footer-actions">
                <button
                  className="class-exams-footer-btn"
                  onClick={() =>
                    navigate("/exam-records", {
                      state: { className: "All Classes" },
                    })
                  }
                >
                  <FontAwesomeIcon icon={faFileExcel} />
                  <span>Export All Data</span>
                </button>
                <button
                  className="class-exams-footer-btn"
                  onClick={() => navigate("/Notifications")}
                >
                  <FontAwesomeIcon icon={faBell} />
                  <span>Get Notifications</span>
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ManageClassExams;